import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Container,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Quiz as QuizIcon,
  PlayArrow as PlayIcon,
  // Live as LiveIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  LiveTv as LiveIcon,
  FlashOn as RapidIcon,
  Bolt as FlashIcon
} from '@mui/icons-material';
import UserAuthButton from '../auth/UserAuthButton';
import AuthModal from '../auth/AuthModal';
import { useAuthContext } from '../../../context/AuthContext';

const QuizWelcome = ({ onSelectMode, quizMode }) => {
  const { isAuthenticated } = useAuthContext();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedMode, setSelectedMode] = useState(quizMode);
  const [pendingFlashMode, setPendingFlashMode] = useState('flash');

  useEffect(() => {
    if (quizMode) {
      setSelectedMode(quizMode);
    }
  }, [quizMode]);

  const handleNormalModeClick = () => {
    onSelectMode('normal');
  };

  const handleLiveModeClick = () => {
    onSelectMode('live');
  };

  const handleRapidModeClick = () => {
    onSelectMode('rapid');
  };

  const handleFlashModeClick = () => {
    if (!isAuthenticated) {
      setPendingFlashMode('flash');
      setShowAuthModal(true);
      return;
    }
    onSelectMode('flash');
  };

  const handleFlashPracticeClick = () => {
    if (!isAuthenticated) {
      setPendingFlashMode('flash-practice');
      setShowAuthModal(true);
      return;
    }
    onSelectMode('flash-practice');
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    onSelectMode(pendingFlashMode);
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    // Don't redirect if user cancels authentication
  };

  const handleHistoryClick = () => {
    window.location.href = '/quiz/history';
  };

  return (
    <>
      <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {selectedMode === 'flash' ? 'Insta Quiz' : 'Quiz'}
          </Typography>
          <UserAuthButton />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <QuizIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Welcome to {selectedMode === 'flash' ? 'Insta Quiz' : 'Quiz'}
          </Typography>
          {/* <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Test your knowledge with our interactive quiz system. Choose your preferred mode and start your quiz journey!
          </Typography> */}
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {/* Normal Mode Card */}
          {selectedMode !== 'flash' && (
            <>
              <Grid item xs={12} md={6} lg={4}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .mode-button': {
                        backgroundColor: 'primary.main',
                        color: 'white'
                      }
                    },
                    border: '2px solid',
                    borderColor: 'primary.light',
                    position: 'relative'
                  }}
                  onClick={handleNormalModeClick}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <PlayIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                    </Box>

                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Normal Mode
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                      Practice mode where you can configure your quiz settings, test your knowledge, and track your progress locally.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                          <Chip
                            icon={<TimerIcon />}
                            label="Timed Questions"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<TrophyIcon />}
                            label="Score Tracking"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Features:
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 3 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Customizable difficulty levels
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Multiple topic categories
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Flexible quiz duration
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Detailed performance analysis
                      </Typography>
                    </Box>

                    <Button
                      className="mode-button"
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<PlayIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Start Normal Mode
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card
                  onClick={handleLiveModeClick}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .mode-button': {
                        backgroundColor: 'secondary.main',
                        color: 'white'
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <LiveIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
                    </Box>

                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Live Mode
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                      Real-time multiplayer quiz mode where you can compete with other players in live quiz sessions.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                          <Chip
                            icon={<GroupIcon />}
                            label="Multiplayer"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<LiveIcon />}
                            label="Real-time"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<TrophyIcon />}
                            label="Leaderboard"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Features:
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 3 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Real-time competition with other players
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Live leaderboard updates
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Synchronized quiz sessions
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Instant results and rankings
                      </Typography>
                    </Box>

                    <Button
                      className="mode-button"
                      variant="contained"
                      color="secondary"
                      size="large"
                      fullWidth
                      startIcon={<PlayIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Live Quiz
                    </Button>
                  </CardContent>

                </Card>
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Card
                  onClick={handleRapidModeClick}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .mode-button': {
                        backgroundColor: 'warning.main',
                        color: 'white'
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <RapidIcon sx={{ fontSize: 60, color: 'warning.main' }} />
                    </Box>

                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Rapid Quiz
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                      Fast-paced quiz mode with admin-controlled question progression. Answer questions as they appear in real-time!
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                          <Chip
                            icon={<RapidIcon />}
                            label="Fast Paced"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<GroupIcon />}
                            label="Multiplayer"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<TrophyIcon />}
                            label="Live Leaderboard"
                            size="small"
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Features:
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 3 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Admin-controlled question timing
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Real-time multiplayer competition
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Live leaderboard updates
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Instant results and rankings
                      </Typography>
                    </Box>

                    <Button
                      className="mode-button"
                      variant="contained"
                      color="warning"
                      size="large"
                      fullWidth
                      startIcon={<PlayIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Rapid Quiz
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Flash Quiz Mode Card */}
              <Grid item xs={12} md={6} lg={4}>
                <Card
                  onClick={handleFlashModeClick}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6,
                      '& .mode-button': {
                        backgroundColor: '#9C27B0',
                        color: 'white'
                      }
                    },
                    border: '2px solid #9C27B0'
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <FlashIcon sx={{ fontSize: 60, color: '#9C27B0' }} />
                    </Box>

                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Insta Quiz
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                      Ultra-responsive quiz mode with instant question delivery. Admin pushes questions and you play immediately!
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Grid container spacing={1} justifyContent="center">
                        <Grid item>
                          <Chip
                            icon={<FlashIcon />}
                            label="Instant"
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<GroupIcon />}
                            label="Multiplayer"
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            icon={<TrophyIcon />}
                            label="Leaderboard"
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#9C27B0', color: '#9C27B0' }}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Features:
                    </Typography>
                    <Box component="ul" sx={{ textAlign: 'left', pl: 2, mb: 3 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Instant question delivery with timed delays
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Admin-controlled question pushing
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Real-time multiplayer competition
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Live leaderboard and instant results
                      </Typography>
                    </Box>

                    <Button
                      className="mode-button"
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<PlayIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        bgcolor: '#9C27B0',
                        '&:hover': {
                          bgcolor: '#7B1FA2'
                        }
                      }}
                    >
                      Insta Quiz
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

            </>
          )}
          {selectedMode === 'flash' && (
            <Grid item xs={12} md={8} lg={6}>
              <Card
                onClick={handleFlashModeClick}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    '& .mode-button': {
                      backgroundColor: '#9C27B0',
                      color: 'white'
                    }
                  },
                  border: '2px solid #9C27B0'
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <LiveIcon sx={{ fontSize: 60, color: '#00c853' }} />
                  </Box>

                  <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Play Live
                  </Typography>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    Join the real Insta Quiz session. Compete with other players in real-time when a host is live.
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={1} justifyContent="center">
                      <Grid item>
                        <Chip icon={<LiveIcon />} label="Live" size="small" variant="outlined" sx={{ borderColor: '#00c853', color: '#00c853' }} />
                      </Grid>
                      <Grid item>
                        <Chip icon={<GroupIcon />} label="Multiplayer" size="small" variant="outlined" sx={{ borderColor: '#9C27B0', color: '#9C27B0' }} />
                      </Grid>
                      <Grid item>
                        <Chip icon={<TrophyIcon />} label="Real Leaderboard" size="small" variant="outlined" sx={{ borderColor: '#9C27B0', color: '#9C27B0' }} />
                      </Grid>
                    </Grid>
                  </Box>

                  <Button
                    className="mode-button"
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<LiveIcon />}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      bgcolor: '#00c853',
                      '&:hover': { bgcolor: '#00a844' }
                    }}
                  >
                    Play Live
                  </Button>

                  <Typography
                    component="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFlashPracticeClick();
                    }}
                    sx={{
                      mt: 2,
                      border: 'none',
                      bgcolor: 'transparent',
                      color: '#9C27B0',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': { color: '#7B1FA2' },
                    }}
                  >
                    New here? Try Demo Mode
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>




        <Box sx={{ textAlign: 'center', mt: 6 }}>
          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose your preferred quiz mode to get started. Normal Mode is perfect for practice and skill building!
          </Typography> */}

          {/* Quiz History Button */}
          {selectedMode !== 'flash' && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleHistoryClick}
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.light',
                  color: 'primary.dark'
                }
              }}
            >
              View Quiz History
            </Button>
          )}
        </Box>
      </Container>

      {/* Auth Modal for Flash Mode */}
      <AuthModal
        open={showAuthModal}
        onClose={handleAuthModalClose}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
};

export default QuizWelcome;
